import React from 'react'
import { Box, LinearProgress, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material'

import { IDwUsageCosts, formatDwDay, formatEurCost } from '../../Services/datawarehouse'

interface Props {
	costs: IDwUsageCosts
}

// Le détail d'une période : total, écart avec la précédente, et répartition par
// service Google.
//
// Le même composant sert la fenêtre par défaut et un mois choisi — c'est
// délibéré : deux rendus finiraient par diverger, et l'écran dirait deux choses
// selon la période.

// #region trend
// trend compare à la fenêtre précédente. Rien à afficher quand elle est absente.
const trend = (costs: IDwUsageCosts): string | null => {
	if (costs.previousEur === undefined || costs.previousEur === null) return null
	// Partir de zéro n'est pas une hausse « infinie » : on le dit en clair.
	if (costs.previousEur === 0) return costs.totalEur > 0 ? 'nouvelle dépense' : null
	const pct = ((costs.totalEur - costs.previousEur) / costs.previousEur) * 100
	const sign = pct >= 0 ? '+' : ''
	return `${sign}${pct.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} % vs période précédente`
}

// #endregion

// #region DwCostsDetail
export const DwCostsDetail: React.FC<Props> = ({ costs }) => {
	const max = costs.byService.reduce((m, s) => Math.max(m, s.costEur), 0)
	const delta = trend(costs)

	return (
		<Box>
			<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
				{formatDwDay(costs.from)} → {formatDwDay(costs.to)}
			</Typography>

			<Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, flexWrap: 'wrap', mb: 2 }}>
				<Typography variant="h4">{formatEurCost(costs.totalEur)}</Typography>
				{delta && (
					<Typography variant="body2" color="text.secondary">
						{delta}
					</Typography>
				)}
				{/* ⚠️ Pas de couleur d'alerte : l'essentiel de cette part est
				    l'infrastructure de Quanti, non rattachée par nature — 73,88 €
				    sur 80,80 € en staging. Ce qui mérite un regard, c'est sa
				    VARIATION, pas son existence. */}
				{costs.unattachedEur > 0 && (
					<Typography variant="body2" color="text.secondary">
						dont {formatEurCost(costs.unattachedEur)} hors projets Quanti (infrastructure comprise)
					</Typography>
				)}
			</Box>

			{costs.byService.length === 0 ? (
				<Typography variant="caption" color="text.secondary">
					Aucune dépense facturée sur la période.
				</Typography>
			) : (
				<Table size="small">
					<TableBody>
						{costs.byService.map((s) => (
							<TableRow key={s.service}>
								<TableCell sx={{ width: '35%' }}>{s.service}</TableCell>
								<TableCell>
									<LinearProgress
										variant="determinate"
										value={max > 0 ? (s.costEur / max) * 100 : 0}
										sx={{ height: 8, borderRadius: 1 }}
									/>
								</TableCell>
								<TableCell align="right" sx={{ width: 120 }}>
									{formatEurCost(s.costEur)}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			)}
		</Box>
	)
}

// #endregion
